import { Clipboard } from "@angular/cdk/clipboard";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, DoCheck, EventEmitter, inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { ExportProductListService } from "@components/project-detail-header/export-product-list.service";
import { ProjectVersionModalComponent } from "@components/project-version-modal/project-version-modal.component";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService } from "@odx/angular/components/modal";
import { PdfService } from "@pdf/pdf.service";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ReadAccessModalService } from "@utils/modals/read-access-modal.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { filter, map, Observable } from "rxjs";
import { Breakpoints } from "../../styles/breakpoints";

@Component({
  selector: "app-project-detail-header",
  templateUrl: "./project-detail-header.component.html",
  styleUrls: ["./project-detail-header.component.scss"],
  standalone: true,
  imports: [UiKitModule],
})
export class ProjectDetailHeaderComponent implements OnChanges, DoCheck {
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private toastHelper = inject(ToastHelper);
  private exportProjectService = inject(ExportProductListService);
  private pdfService = inject(PdfService);
  private readAccessModalService = inject(ReadAccessModalService);
  private floorplanService = inject(FloorplanService, { optional: true });
  private breakpointObserver = inject(BreakpointObserver);
  private localeId = inject(LOCALE_ID);
  private clipboard = inject(Clipboard);

  @Input({ required: true }) project!: ScopedProject;
  @Input() activeFloorplan?: Floorplan;
  @Input() hideBackArrow?: boolean = false;
  @Output() navigateBack = new EventEmitter<void>();

  private readonly modalService = inject(ModalService);

  protected readonly navigator = navigator;
  protected floorplanExportEnabled: boolean = false;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected isMobile$ = this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches));
  protected localizedLastModified = "";
  protected localizedLastCloudSync = "";
  private lastModifiedTimestamp?: number;
  private lastCloudSyncTimestamp?: number;

  protected get sharedProjectLink(): string | undefined {
    return this.project?.cloudId ? this.projectService.getSharedProjectLink(this.project.cloudId) : undefined;
  }
  protected copyProjectLinkTooltip: string = $localize`:@@project.btn.copy.share.link:Kopiert einen teilbaren Link zu diesem Projekt. Die URL in der Adressleiste des Browsers kann aktuell aus technischen Gründen nicht zum Teilen verwendet werden.`;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    this.floorplanExportEnabled = !!this.floorplanService;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["project"]?.currentValue) {
      this.updateLocalizedTimestamps();
    }
  }

  ngDoCheck(): void {
    if (!this.project) {
      return;
    }

    const currentLastModified = this.project.lastModified.getTime();
    const currentLastCloudSync = this.project.lastCloudSync?.getTime();

    if (currentLastModified !== this.lastModifiedTimestamp || currentLastCloudSync !== this.lastCloudSyncTimestamp) {
      this.updateLocalizedTimestamps();
    }
  }

  private updateLocalizedTimestamps(): void {
    this.lastModifiedTimestamp = this.project.lastModified.getTime();
    this.lastCloudSyncTimestamp = this.project.lastCloudSync?.getTime();

    const modifiedDate = this.project.lastModified.toLocaleDateString(this.localeId);
    const modifiedTime = this.project.lastModified.toLocaleTimeString(this.localeId);
    this.localizedLastModified = $localize`:@@projectDetailHeader.lastModified.mobile:Letzte Änderung: ${modifiedDate}, ${modifiedTime}`;

    if (!this.project.lastCloudSync) {
      this.localizedLastCloudSync = $localize`:@@projectDetailHeader.lastSavedToCloud.mobile.noCloudData:Letzte Speicherung in der Cloud: --`;
      return;
    }

    const cloudSyncDate = this.project.lastCloudSync.toLocaleDateString(this.localeId);
    const cloudSyncTime = this.project.lastCloudSync.toLocaleTimeString(this.localeId);
    this.localizedLastCloudSync = $localize`:@@projectDetailHeader.lastSavedToCloud.mobile:Letzte Speicherung in der Cloud: ${cloudSyncDate}, ${cloudSyncTime}`;
  }

  exportProductList(project: ScopedProject) {
    this.exportProjectService.export(project);
  }

  exportProjectToPdf(project: ScopedProject) {
    this.modalService.open(ProjectVersionModalComponent, { data: { project: project } }).onClose$.subscribe();
  }

  exportFloorplanToPdf() {
    if (!this.activeFloorplan) {
      return;
    }
    this.toastHelper.message(
      $localize`:@@toast.downloadFloorplanStarted: PDF Export gestartet`,
      $localize`:@@toast.StartedDownloadFloorplanDescription: Der Grundrissplan ${this.activeFloorplan?.name} wird als PDF exportiert.`,
    );
    this.pdfService.exportFloorplanToPdf(this.activeFloorplan).subscribe({
      next: ({ pdf, fileName }) => pdf.save(fileName),
      error: () =>
        this.toastHelper.error(
          $localize`:@@toast.FloorplanDownloadFailed:PDF Export fehlgeschlagen`,
          $localize`:@@toast.FloorplanFailedDownloadDescription:Der Grundrissplan ${this.activeFloorplan?.name} konnte nicht exportiert werden.`,
        ),
      complete: () =>
        this.toastHelper.success(
          $localize`:@@toast.FloorplanSuccessfulDownloaded:PDF Export erfolgreich`,
          $localize`:@@toast.FloorplanSuccessfulDownloadDescription:Der Grundrissplan ${this.activeFloorplan?.name} wurde erfolgreich als PDF exportiert.`,
        ),
    });
  }

  onNavigateBack() {
    this.navigateBack.emit();
  }

  openShareLinkModal() {
    this.readAccessModalService.openModal(this.project);
  }
  protected copyProjectLink() {
    const success = !!this.sharedProjectLink && this.clipboard.copy(this.sharedProjectLink);

    this.toastHelper[success ? "success" : "error"](
      success
        ? $localize`:@@toast.project.linkToClipboard.success: Der Link zum Projekt wurde in die Zwischenablage kopiert`
        : $localize`:@@toast.project.link.copied: Der Link zum Projekt konnte nicht in die Zwischenablage kopiert werden.`,
    );
  }
}
